// SPDX-License-Identifier: LicenseRef-Proprietary

import { useEffect, useRef, useCallback } from 'react';
import { useVaccineDashboardStore } from '../store/vaccineDashboardStore';
import { useVaccineClientStore } from '../store/vaccineClientStore';
import { useDecisionTreeComputation } from './useDecisionTreeComputation';
import { prepareComputationData } from '../facade/vaccineFacade';

/**
 * Hook to automatically trigger backend computation when inputs or tree change
 * Uses debouncing to prevent infinite rerenders and excessive API calls
 */
export function useAutoBackendComputation(debounceMs: number = 1000) {
  const inputs = useVaccineDashboardStore((state) => state.inputs);
  const isDashboardImporting = useVaccineDashboardStore((state) => state.isImporting);
  const nodes = useVaccineClientStore((state) => state.nodes);
  const edges = useVaccineClientStore((state) => state.edges);
  const costParameters = useVaccineClientStore((state) => state.costParameters);
  const populationParameters = useVaccineClientStore((state) => state.populationParameters);
  const isTreeImporting = useVaccineClientStore((state) => state.isImporting);
  
  const { compute, loading, canCompute } = useDecisionTreeComputation();
  
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousHashRef = useRef<string | null>(null); // null = never run, string = has run
  const isInitialComputationTriggeredRef = useRef(false);

  // Helper function to compute hash
  const computeHash = useCallback(() => {
    return JSON.stringify({
      inputs: {
        perspective: inputs.perspective,
        population: inputs.population,
        vaccineEfficacyPct: inputs.vaccineEfficacyPct,
        vaccineCostPerDoseEUR: inputs.vaccineCostPerDoseEUR,
        vaccinePriceDiscountPct: inputs.vaccinePriceDiscountPct,
        treatmentCostPerCaseEUR: inputs.treatmentCostPerCaseEUR,
        baselineIncidencePer100k: inputs.baselineIncidencePer100k,
        dosesPerCourse: inputs.dosesPerCourse,
        adminCostPerDoseEUR: inputs.adminCostPerDoseEUR,
        productivityLossDaysPerCase: inputs.productivityLossDaysPerCase,
        productivityValuePerDayEUR: inputs.productivityValuePerDayEUR,
        timeHorizonYears: inputs.timeHorizonYears,
        discountRatePct: inputs.discountRatePct,
        discountingEnabled: inputs.discountingEnabled,
        boosterYears: inputs.boosterYears,
        boosterCost: inputs.boosterCost,
        coveragePctByRegion: inputs.coveragePctByRegion,
        defaultCoveragePct: inputs.defaultCoveragePct,
        utilityInfection: inputs.utilityInfection,
        durationInfectionDays: inputs.durationInfectionDays,
        hospitalizationRate: inputs.hospitalizationRate,
        baselineMortalityRate: inputs.baselineMortalityRate,
        waningPctPerYear: inputs.waningPctPerYear,
        adverseEventRate: inputs.adverseEventRate,
        adverseEventCost: inputs.adverseEventCost,
        adverseEventQalyLoss: inputs.adverseEventQalyLoss,
        hospitalizationCostPerCaseEUR: inputs.hospitalizationCostPerCaseEUR,
        durationHospitalizationDays: inputs.durationHospitalizationDays,
        utilityHospitalization: inputs.utilityHospitalization,
        incidenceMode: inputs.incidenceMode,
        incidenceBandsPer100k: inputs.incidenceBandsPer100k,
        cohortStartingAge: inputs.cohortStartingAge,
        qalyLossMode: inputs.qalyLossMode,
        qalyLossBands: inputs.qalyLossBands,
        backgroundUtilitySource: inputs.backgroundUtilitySource,
        mortalitySource: inputs.mortalitySource,
        waningMode: inputs.waningMode,
        veSchedule: inputs.veSchedule,
        complications: inputs.complications,
      },
      nodeCount: Object.keys(nodes).length,
      edgeCount: edges.length,
      costParams: costParameters,
      popParams: populationParameters,
    });
  }, [
    inputs.perspective,
    inputs.population,
    inputs.vaccineEfficacyPct,
    inputs.vaccineCostPerDoseEUR,
    inputs.vaccinePriceDiscountPct,
    inputs.treatmentCostPerCaseEUR,
    inputs.baselineIncidencePer100k,
    inputs.dosesPerCourse,
    inputs.adminCostPerDoseEUR,
    inputs.productivityLossDaysPerCase,
    inputs.productivityValuePerDayEUR,
    inputs.timeHorizonYears,
    inputs.discountRatePct,
    inputs.discountingEnabled,
    inputs.boosterYears,
    inputs.boosterCost,
    inputs.coveragePctByRegion,
    inputs.defaultCoveragePct,
    inputs.utilityInfection,
    inputs.durationInfectionDays,
    inputs.hospitalizationRate,
    inputs.baselineMortalityRate,
    inputs.waningPctPerYear,
    inputs.adverseEventRate,
    inputs.adverseEventCost,
    inputs.adverseEventQalyLoss,
    inputs.hospitalizationCostPerCaseEUR,
    inputs.durationHospitalizationDays,
    inputs.utilityHospitalization,
    inputs.incidenceMode,
    inputs.incidenceBandsPer100k,
    inputs.cohortStartingAge,
    inputs.qalyLossMode,
    inputs.qalyLossBands,
    inputs.backgroundUtilitySource,
    inputs.mortalitySource,
    inputs.waningMode,
    inputs.veSchedule,
    inputs.complications,
    nodes,
    edges,
    costParameters,
    populationParameters,
  ]);

  // Helper function to trigger computation (consolidates all hash + guard logic)
  const triggerComputation = useCallback((reason: string) => {
    // Skip sync during import hydration to prevent interference
    if (isDashboardImporting || isTreeImporting) {
      console.log(`[AutoCompute] Skipping compute: import in progress`, reason);
      return;
    }

    if (loading || !canCompute) {
      console.log(`[AutoCompute] Skipping compute: loading=${loading}, canCompute=${canCompute}`, reason);
      return;
    }

    const currentHash = computeHash();

    // Allow first ever computation regardless of hash
    if (previousHashRef.current !== null && previousHashRef.current === currentHash) {
      console.log('[AutoCompute] Skipping compute (same hash)', reason);
      return;
    }

    console.log('[AutoCompute] Running compute', { reason, currentHash, previousHash: previousHashRef.current });

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Guard: Skip autosync/computation during import to prevent side effects
      const importing =
        useVaccineDashboardStore.getState().isImporting ||
        useVaccineClientStore.getState().isImporting;
      
      if (importing) {
        console.log('[AutoCompute] Skipping compute during import hydration');
        return; // skip autosync/computation during import
      }

      // Sync dashboard inputs to decision tree store before computing
      prepareComputationData();

      useVaccineClientStore.getState().validate();
      
      const issues = useVaccineClientStore.getState().issues;
      const hasErrors = issues.some(i => i.severity === 'error');
      
      if (hasErrors) {
         console.warn('[AutoCompute] Aborting computation due to validation errors');
         return;
      }

      compute();
      // Set hash AFTER compute call (will be set when mutation starts)
      previousHashRef.current = currentHash;
    }, debounceMs);
  }, [canCompute, loading, compute, computeHash, debounceMs, isDashboardImporting, isTreeImporting]);

  // Handle initial mount: trigger computation when tree is ready (reactive to canCompute)
  useEffect(() => {
    if (isInitialComputationTriggeredRef.current) return;
    if (!canCompute || loading) return;
    // Skip during import hydration
    if (isDashboardImporting || isTreeImporting) return;

    isInitialComputationTriggeredRef.current = true;
    triggerComputation('initial page load');
  }, [canCompute, loading, triggerComputation, isDashboardImporting, isTreeImporting]);

  // Handle subsequent changes
  useEffect(() => {
    // Only run after initial computation has been triggered
    if (!isInitialComputationTriggeredRef.current) {
      return;
    }
    // Skip during import hydration
    if (isDashboardImporting || isTreeImporting) {
      return;
    }

    triggerComputation('state change');
  }, [
    inputs.perspective,
    inputs.population,
    inputs.vaccineEfficacyPct,
    inputs.vaccineCostPerDoseEUR,
    inputs.vaccinePriceDiscountPct,
    inputs.treatmentCostPerCaseEUR,
    inputs.baselineIncidencePer100k,
    inputs.dosesPerCourse,
    inputs.adminCostPerDoseEUR,
    inputs.productivityLossDaysPerCase,
    inputs.productivityValuePerDayEUR,
    inputs.timeHorizonYears,
    inputs.discountRatePct,
    inputs.discountingEnabled,
    inputs.boosterYears,
    inputs.boosterCost,
    inputs.coveragePctByRegion,
    inputs.defaultCoveragePct,
    inputs.utilityInfection,
    inputs.durationInfectionDays,
    inputs.hospitalizationRate,
    inputs.baselineMortalityRate,
    inputs.waningPctPerYear,
    inputs.adverseEventRate,
    inputs.adverseEventCost,
    inputs.adverseEventQalyLoss,
    inputs.hospitalizationCostPerCaseEUR,
    inputs.durationHospitalizationDays,
    inputs.utilityHospitalization,
    inputs.incidenceMode,
    inputs.incidenceBandsPer100k,
    inputs.cohortStartingAge,
    inputs.qalyLossMode,
    inputs.qalyLossBands,
    inputs.backgroundUtilitySource,
    inputs.mortalitySource,
    inputs.waningMode,
    inputs.veSchedule,
    inputs.complications,
    nodes,
    edges,
    costParameters,
    populationParameters,
    triggerComputation,
    isDashboardImporting,
    isTreeImporting,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
}
